<template>
  <div class="campaigns__title-row">
    <div class="campaigns__titlebar">
      <h2 class="campaigns__title">Кампании</h2>
    </div>
    <div class="campaigns__meta">
      <input v-model.trim="query" type="text" class="input input--search" placeholder="Поиск по названию"
        aria-label="Поиск по названию" />
      <select v-model="statusFilter" class="select" aria-label="Фильтр по статусу">
        <option value="">All Statuses</option>
        <option value="active">Active</option>
        <option value="paused">Paused</option>
        <option value="finished">Finished</option>
        <option value="moderation">Moderation</option>
      </select>
      <!-- Объединённый фильтр: рекламодатели и бренды (мультивыбор, бренды вложены в рекламодателей) -->
      <div v-if="advertisersWithBrands.length > 0" class="filter-advertiser-brand" ref="filterDropdownRef">
        <button type="button" class="filter-advertiser-brand__trigger select"
          :class="{ 'filter-advertiser-brand__trigger--active': hasActiveFilter }"
          @click="filterDropdownOpen = !filterDropdownOpen"
          aria-haspopup="listbox" :aria-expanded="filterDropdownOpen"
          aria-label="Фильтр по рекламодателям и брендам">
          {{ filterTriggerLabel }}
        </button>
        <div v-show="filterDropdownOpen" class="filter-advertiser-brand__panel" role="listbox">
          <div class="filter-advertiser-brand__list">
            <template v-for="adv in advertisersWithBrands" :key="adv.id">
              <label class="filter-advertiser-brand__item filter-advertiser-brand__item--advertiser">
                <input type="checkbox" :checked="isAdvertiserSelected(adv.id)"
                  @change="toggleAdvertiser(adv.id)" />
                <span class="filter-advertiser-brand__label">{{ adv.name }}</span>
              </label>
              <div v-if="adv.brands && adv.brands.length" class="filter-advertiser-brand__brands">
                <label v-for="brand in adv.brands" :key="brand.id"
                  class="filter-advertiser-brand__item filter-advertiser-brand__item--brand">
                  <input type="checkbox" :checked="isBrandSelected(brand.id)"
                    @change="toggleBrand(brand.id)" />
                  <span class="filter-advertiser-brand__label">{{ brand.name }}</span>
                </label>
              </div>
            </template>
          </div>
          <div class="filter-advertiser-brand__actions">
            <button type="button" class="filter-advertiser-brand__clear" @click="clearAdvertiserBrandFilter">
              Сбросить
            </button>
          </div>
        </div>
      </div>
      <div class="sort-controls">
        <select v-model="sortBy" class="select" aria-label="Сортировка">
          <option value="id">По дате создания</option>
          <option value="updated_ms">По дате обновления</option>
          <option value="name">По названию</option>
          <option value="period_start">По дате начала</option>
          <option value="period_end">По дате окончания</option>
        </select>
        <button class="sort-direction-btn" @click="toggleSortDirection"
          :title="sortDirection === 'asc' ? 'По возрастанию' : 'По убыванию'"
          aria-label="Изменить направление сортировки">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
            class="sort-arrow" :class="{ 'sort-arrow--asc': sortDirection === 'asc' }">
            <path d="M8 12L4 8H7V2H9V8H12L8 12Z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CampaignsHeader',
  props: {
    isAdmin: {
      type: Boolean,
      required: true
    },
    syncing: {
      type: Boolean,
      required: true
    },
    statusDict: {
      type: Object,
      required: true
    },
    advertisers: {
      type: Array,
      default: () => []
    },
    brands: {
      type: Array,
      default: () => []
    },
    selectedAdvertiserIds: {
      type: Array,
      default: () => []
    },
    selectedBrandIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:query', 'update:statusFilter', 'update:selectedAdvertiserIds', 'update:selectedBrandIds', 'update:sortBy', 'update:sortDirection'],
  data() {
    return {
      query: '',
      statusFilter: '',
      sortBy: 'id',
      sortDirection: 'desc',
      filterDropdownOpen: false
    }
  },
  computed: {
    advertisersWithBrands() {
      const advertisers = this.advertisers || [];
      const brands = this.brands || [];
      return advertisers.map((adv) => ({
        ...adv,
        brands: brands.filter((b) => Number(b.advertiser_id) === Number(adv.id))
      }));
    },
    hasActiveFilter() {
      return (this.selectedAdvertiserIds && this.selectedAdvertiserIds.length > 0) ||
        (this.selectedBrandIds && this.selectedBrandIds.length > 0);
    },
    filterTriggerLabel() {
      if (!this.hasActiveFilter) return 'Рекламодатели и бренды';
      const parts = [];
      if (this.selectedAdvertiserIds && this.selectedAdvertiserIds.length > 0) {
        parts.push(`Рекл.: ${this.selectedAdvertiserIds.length}`);
      }
      if (this.selectedBrandIds && this.selectedBrandIds.length > 0) {
        parts.push(`Бренды: ${this.selectedBrandIds.length}`);
      }
      return parts.join(' · ');
    }
  },
  methods: {
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      this.$emit('update:sortDirection', this.sortDirection);
    },
    isAdvertiserSelected(id) {
      return (this.selectedAdvertiserIds || []).indexOf(id) !== -1 ||
        (this.selectedAdvertiserIds || []).some((x) => Number(x) === Number(id));
    },
    isBrandSelected(id) {
      return (this.selectedBrandIds || []).indexOf(id) !== -1 ||
        (this.selectedBrandIds || []).some((x) => Number(x) === Number(id));
    },
    toggleAdvertiser(id) {
      const ids = this.selectedAdvertiserIds ? [...this.selectedAdvertiserIds] : [];
      const numId = Number(id);
      const idx = ids.findIndex((x) => Number(x) === numId);
      if (idx === -1) ids.push(numId);
      else ids.splice(idx, 1);
      this.$emit('update:selectedAdvertiserIds', ids);
    },
    toggleBrand(id) {
      const ids = this.selectedBrandIds ? [...this.selectedBrandIds] : [];
      const numId = Number(id);
      const idx = ids.findIndex((x) => Number(x) === numId);
      if (idx === -1) ids.push(numId);
      else ids.splice(idx, 1);
      this.$emit('update:selectedBrandIds', ids);
    },
    clearAdvertiserBrandFilter() {
      this.$emit('update:selectedAdvertiserIds', []);
      this.$emit('update:selectedBrandIds', []);
    },
    onDocumentClick(e) {
      if (this.filterDropdownOpen && this.$refs.filterDropdownRef && !this.$refs.filterDropdownRef.contains(e.target)) {
        this.filterDropdownOpen = false;
      }
    }
  },
  watch: {
    query(newValue) {
      this.$emit('update:query', newValue)
    },
    statusFilter(newValue) {
      this.$emit('update:statusFilter', newValue)
    },
    sortBy(newValue) {
      this.$emit('update:sortBy', newValue)
    },
    sortDirection(newValue) {
      this.$emit('update:sortDirection', newValue)
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  }
}
</script>

<style scoped lang="scss">
.filter-advertiser-brand {
  position: relative;
}

.filter-advertiser-brand__trigger {
  min-width: 160px;
  text-align: left;
  cursor: pointer;
  appearance: auto;

  &.filter-advertiser-brand__trigger--active {
    border-color: var(--accent, #3b82f6);
    background: rgba(59, 130, 246, 0.06);
  }
}

.filter-advertiser-brand__panel {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 260px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid var(--card-border, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
}

.filter-advertiser-brand__list {
  padding: 8px 0;
}

.filter-advertiser-brand__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: var(--font-size-sm);
  user-select: none;

  &:hover {
    background: var(--surface, #f8f9fa);
  }

  input[type="checkbox"] {
    flex-shrink: 0;
    cursor: pointer;
  }

  &.filter-advertiser-brand__item--advertiser {
    font-weight: 600;
    color: var(--accent, #1e293b);
  }

  &.filter-advertiser-brand__item--brand {
    padding-left: 28px;
    font-weight: 400;
    color: var(--muted, #64748b);
  }
}

.filter-advertiser-brand__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-advertiser-brand__brands {
  display: flex;
  flex-direction: column;
}

.filter-advertiser-brand__actions {
  padding: 8px 12px;
  border-top: 1px solid var(--card-border, #e2e8f0);
}

.filter-advertiser-brand__clear {
  padding: 4px 8px;
  font-size: var(--font-size-sm);
  color: var(--muted, #64748b);
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--surface, #f1f5f9);
    color: var(--accent, #3b82f6);
  }
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sort-direction-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--card-border, #e2e8f0);
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--accent, #3b82f6);

  &:hover {
    background: #f8fafc;
    border-color: var(--accent, #3b82f6);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  .sort-arrow {
    transition: transform 0.2s ease;

    &.sort-arrow--asc {
      transform: rotate(180deg);
    }
  }
}
</style>
